import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Box, Heading, Text, Button, VStack } from "@chakra-ui/react";
import { logger } from "@/utils/logger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch and handle errors in the component tree
 * Logs errors and displays a fallback UI
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error("Error caught by Error Boundary", error, {
      component: "ErrorBoundary",
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          padding={4}
          bg="gray.50"
        >
          <VStack gap={4} maxWidth="500px" textAlign="center">
            <Heading size="xl" color="red.500">
              Oops! Something went wrong
            </Heading>
            <Text color="gray.600">
              We're sorry, but something unexpected happened. Please try refreshing the page or
              contact support if the problem persists.
            </Text>
            {import.meta.env.MODE !== "production" && this.state.error && (
              <Box
                bg="gray.100"
                padding={4}
                borderRadius="md"
                width="100%"
                textAlign="left"
                overflow="auto"
              >
                <Text fontSize="sm" fontFamily="mono" color="red.600">
                  {this.state.error.message}
                </Text>
              </Box>
            )}
            <Button colorScheme="blue" onClick={this.handleReset}>
              Try Again
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
